import { useEffect, useState } from "react";
import {
    MdDescription,
    MdFilterList,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Spec } from "../../models/specs";
import { SpecsFilters, SpecsFiltersType } from "./SpecsFilterList";
import { SpecCard } from "./SpecCard";
import { useSpecSpaceStore } from "../../store/SpecSpaceStore";
import SpecsFilterBar from "./SpecsFilterBar";
import toast from "react-hot-toast";
import { getAllSpecs } from "../../api/specs";

type SpecSpaceProps = {
    mode: "user" | "admin";
};

export default function SpecsSpace({ mode }: SpecSpaceProps) {
    const {
        currentParams,
        setCurrentParams,
        filteredSpecs,
        setFilteredSpecs,
        activeTab,
        setActiveTab,
        loading,
        setLoading,
        err,
        setErr,
        showFilters,
        setShowFilters,
        currentFilters,
        setCurrentFilters,
        selectedProduct,
        setSelectedProduct,
        searchText,
        setSearchText
    } = useSpecSpaceStore();

    const [debouncedSearch, setDebouncedSearch] = useState(searchText);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchText);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchText]);

    useEffect(() => {
        setCurrentParams({
            ...currentParams,
            category: debouncedSearch || undefined,
        });
    }, [debouncedSearch]);

    const fetchSpecs = async (params?: Record<string, any>) => {
        setLoading(true);
        setErr("");
        try {
            let raw: Spec[] = [];
            const p = { ...params };

            raw = await getAllSpecs(p);
            setFilteredSpecs(raw);
            console.log(filteredSpecs)
        } catch (e: any) {
            setErr(e?.message || "Failed to load specs.");
            setFilteredSpecs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSpecs(currentParams);
    }, [currentParams]);


    useEffect(() => {
        setCurrentFilters({
            verticals: [],
            product: []
        });
        setSelectedProduct("All");
        setSearchText("");
        setDebouncedSearch("");
        setShowFilters(false);
    }, [activeTab]);


    const applyFilters = async (params: Record<string, any>) => {
        const newParams = {
            ...params,
            product: selectedProduct === "All" ? [] : [selectedProduct],
            category: searchText
        };
        setCurrentParams(newParams);
        setShowFilters(false);
    };

    const applyFiltersBar = async (params: SpecsFiltersType, product?: string) => {

        const newParams = {
            ...params,
            product: product === "All" ? [] : [product ?? selectedProduct],
            category: searchText
        };

        setCurrentParams(newParams);
        setShowFilters(false);
    };
    const handleView = (spec: Spec) => {
        window.open(`/media/specs/${spec.unique_id}.html`, "_blank");
    };
    const hasActiveFilters =
        (currentFilters.product?.length ?? 0) > 0

    return (
        <div className="bg-[#F3F4F6] min-h-[100vh] flex flex-col">
            <div className="py-2 px-2 w-full max-w-[1400px] mx-auto min-h-screen">
                {/* Header */}
                <div className="flex flex-col gap-4 mb-4">
                    <div className="flex items-center gap-2">
                        <MdDescription size={32} className="text-gray-500" />
                        <h1 className="text-base sm:text-2xl font-semibold text-gray-600 leading-snug font-sans">
                            API Documentation
                        </h1>
                    </div>
                </div>

                {/* Filter Bar */}
                <div
                    className="
      flex flex-col md:flex-row 
      items-stretch md:items-center 
      justify-between gap-3 md:gap-4 
      rounded-xl shadow bg-gray-100 
      p-3 md:p-4 mb-4
    "
                >
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full">

                        {/* Scrollable horizontal filter bar */}
                        <div className="flex overflow-x-auto scrollbar-hide w-full md:w-auto">
                            <SpecsFilterBar
                                options={["All", "Calls", "Leads", "Traffic"]}
                                currentFilter={selectedProduct}
                                onChange={(newValue) => {
                                    setSelectedProduct(newValue);
                                    const newFilters: SpecsFiltersType = {
                                        product: newValue === "All" ? [] : [newValue],
                                        verticals: [],
                                    };
                                    setSearchText("");
                                    setDebouncedSearch("");
                                    setCurrentFilters(newFilters);
                                    applyFiltersBar(newFilters, newValue);
                                }}
                            />
                        </div>
                        <div className="flex-1">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="w-full px-3 py-2 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center justify-center 
            px-4 md:px-6 py-2.5 md:py-3 rounded-xl shadow font-semibold text-sm 
            border transition-colors w-full md:w-auto
            ${hasActiveFilters
                                    ? "bg-[#F97316] text-white border-[#F97316]"
                                    : "bg-white text-[#606060] border-[#ececec] hover:bg-gray-50"
                                }`}
                        >
                            <MdFilterList className="mr-2 text-lg" />
                            Filter
                        </button>

                    </div>
                </div>

                {showFilters && (
                    <div className="relative z-10 flex justify-end mb-4">
                        <SpecsFilters
                            value={currentFilters}
                            onChange={setCurrentFilters}
                            onApply={applyFilters}
                            onClose={() => setShowFilters(false)}
                        />
                    </div>
                )}

                {/* Specs Grid */}
                <div className="flex flex-col gap-4">
                    {loading &&
                        [...Array(8)].map((_, i) => (
                            <div key={i} className="h-[400px] bg-white rounded-2xl shadow animate-pulse" />
                        ))}

                    {!loading && err && (
                        <div className="col-span-full bg-white rounded-2xl shadow p-6 text-center text-red-600 font-semibold">
                            {err}
                        </div>
                    )}




                    {!loading && !err && filteredSpecs.length === 0 && (
                        <div className="col-span-full bg-white rounded-2xl shadow p-10 text-center text-[#606060]">
                            {hasActiveFilters ? "No specs match the selected filters." : "No specs found for this tab."}
                        </div>
                    )}

                    {!loading &&
                        !err &&
                        filteredSpecs.map((spec, i) =>
                            <SpecCard
                                key={spec.unique_id ?? i}
                                spec={spec}
                                onView={handleView}
                            />
                        )}
                </div>
            </div>
        </div>
    );

}
